/**
 * PrintPatientSummaryOverlay Component
 * Generates and prints a comprehensive patient summary document
 */

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Printer, FileText, Download, Loader2, CheckCircle2, User,
  Calendar, Activity, Pill, Heart, AlertTriangle, Phone,
  Home, Users, ClipboardList, Clock, Share2, Mail, Link, Copy
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrintPatientSummaryOverlay({ 
  patientId, 
  patientName,
  onClose 
}) {
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [selectedSections, setSelectedSections] = useState({
    demographics: true,
    diagnoses: true,
    medications: true,
    vitals: true,
    careTeam: true,
    recentVisits: true,
    carePlan: true,
    alerts: true
  });
  const printRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('hospice_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        // Fetch patient EHR summary
        const response = await fetch(
          `${API_URL}/api/ehr/patient-ehr-summary/${patientId}`,
          { headers: getAuthHeaders() }
        );
        
        if (response.ok) {
          const data = await response.json();
          setSummaryData(data);
        } else {
          // Use mock data if API fails
          setSummaryData({
            patient: {
              name: patientName,
              dob: "1945-03-15",
              age: 80,
              gender: "Female",
              address: "123 Care Lane, Hospice City, HC 12345",
              phone: "(555) 123-4567",
              emergency_contact: "John Smith (Son) - (555) 987-6543",
              admission_date: "2025-11-15",
              pps_score: 45,
              primary_diagnosis: "End-stage heart failure",
              attending_physician: "Dr. Sarah Johnson"
            },
            diagnoses: [
              { code: "I50.9", description: "Heart failure, unspecified", primary: true },
              { code: "J44.9", description: "COPD, unspecified", primary: false },
              { code: "E11.9", description: "Type 2 diabetes mellitus", primary: false }
            ],
            medications: [
              { name: "Morphine Sulfate", dose: "15mg", frequency: "Q4H PRN", route: "PO" },
              { name: "Furosemide", dose: "40mg", frequency: "Daily", route: "PO" },
              { name: "Metoprolol", dose: "25mg", frequency: "BID", route: "PO" },
              { name: "Metformin", dose: "500mg", frequency: "BID", route: "PO" }
            ],
            vitals: {
              last_recorded: new Date().toISOString(),
              bp: "118/72",
              hr: "78",
              rr: "18",
              temp: "98.2",
              spo2: "94%",
              pain: "3/10"
            },
            care_team: [
              { role: "Primary RN", name: "Emily Rodriguez, RN" },
              { role: "Attending Physician", name: "Dr. Sarah Johnson" },
              { role: "Social Worker", name: "Michael Chen, MSW" },
              { role: "Chaplain", name: "Rev. Patricia Adams" }
            ],
            recent_visits: [
              { date: "2026-01-30", type: "RN Visit", provider: "Emily Rodriguez", notes: "Patient stable, pain controlled" },
              { date: "2026-01-28", type: "Aide Visit", provider: "Maria Santos", notes: "ADL assistance provided" },
              { date: "2026-01-25", type: "SW Visit", provider: "Michael Chen", notes: "Family meeting completed" }
            ],
            care_plan: {
              goals: ["Pain management", "Symptom control", "Family support"],
              interventions: ["PRN morphine for pain", "Daily vitals monitoring", "Weekly family updates"]
            },
            alerts: [
              { type: "Recertification", message: "Due in 15 days", priority: "medium" },
              { type: "Medication", message: "Morphine refill needed", priority: "high" }
            ]
          });
        }
      } catch (error) {
        console.error("Error fetching summary:", error);
        toast.error("Failed to load patient summary");
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchSummary();
    }
  }, [patientId, patientName]);

  const toggleSection = (section) => {
    setSelectedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handlePrint = () => {
    setPrinting(true);
    
    // Create print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Please allow popups to print");
      setPrinting(false);
      return;
    }

    const printContent = generatePrintHTML();
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
        setPrinting(false);
        toast.success("Print job sent successfully");
      };
    };

    // Fallback timeout
    setTimeout(() => {
      if (printing) {
        setPrinting(false);
      }
    }, 5000);
  };

  const generatePrintHTML = () => {
    const data = summaryData;
    const sections = selectedSections;
    const today = new Date().toLocaleDateString();
    const time = new Date().toLocaleTimeString();

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Patient Summary - ${data?.patient?.name || patientName}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: Arial, sans-serif; 
            font-size: 11pt; 
            line-height: 1.4;
            color: #333;
            padding: 20px;
          }
          .header { 
            border-bottom: 2px solid #0d9488; 
            padding-bottom: 15px; 
            margin-bottom: 20px; 
          }
          .header h1 { 
            font-size: 18pt; 
            color: #0d9488; 
            margin-bottom: 5px;
          }
          .header .subtitle { 
            font-size: 10pt; 
            color: #666; 
          }
          .section { 
            margin-bottom: 20px; 
            page-break-inside: avoid;
          }
          .section-title { 
            font-size: 12pt; 
            font-weight: bold; 
            color: #0d9488; 
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
            margin-bottom: 10px;
          }
          .info-grid { 
            display: grid; 
            grid-template-columns: 1fr 1fr; 
            gap: 8px; 
          }
          .info-item { 
            display: flex; 
          }
          .info-label { 
            font-weight: bold; 
            min-width: 140px; 
            color: #555;
          }
          .info-value { 
            color: #333; 
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 10px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 6px 8px; 
            text-align: left; 
            font-size: 10pt;
          }
          th { 
            background: #f5f5f5; 
            font-weight: bold; 
          }
          .alert { 
            background: #fef3cd; 
            border-left: 3px solid #f59e0b; 
            padding: 8px 12px; 
            margin-bottom: 8px;
          }
          .alert-high { 
            background: #fee2e2; 
            border-left-color: #ef4444; 
          }
          .footer { 
            margin-top: 30px; 
            padding-top: 15px; 
            border-top: 1px solid #ddd; 
            font-size: 9pt; 
            color: #666;
            text-align: center;
          }
          @media print {
            body { padding: 0; }
            .section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Patient Summary Report</h1>
          <div class="subtitle">Generated: ${today} at ${time} | Hospice Care System</div>
        </div>

        ${sections.demographics ? `
        <div class="section">
          <div class="section-title">Patient Demographics</div>
          <div class="info-grid">
            <div class="info-item"><span class="info-label">Patient Name:</span><span class="info-value">${data?.patient?.name || patientName}</span></div>
            <div class="info-item"><span class="info-label">Date of Birth:</span><span class="info-value">${data?.patient?.dob || 'N/A'}</span></div>
            <div class="info-item"><span class="info-label">Age:</span><span class="info-value">${data?.patient?.age || 'N/A'} years</span></div>
            <div class="info-item"><span class="info-label">Gender:</span><span class="info-value">${data?.patient?.gender || 'N/A'}</span></div>
            <div class="info-item"><span class="info-label">Phone:</span><span class="info-value">${data?.patient?.phone || 'N/A'}</span></div>
            <div class="info-item"><span class="info-label">Address:</span><span class="info-value">${data?.patient?.address || 'N/A'}</span></div>
            <div class="info-item"><span class="info-label">Emergency Contact:</span><span class="info-value">${data?.patient?.emergency_contact || 'N/A'}</span></div>
            <div class="info-item"><span class="info-label">Admission Date:</span><span class="info-value">${data?.patient?.admission_date || 'N/A'}</span></div>
            <div class="info-item"><span class="info-label">PPS Score:</span><span class="info-value">${data?.patient?.pps_score || 'N/A'}%</span></div>
            <div class="info-item"><span class="info-label">Attending Physician:</span><span class="info-value">${data?.patient?.attending_physician || 'N/A'}</span></div>
          </div>
        </div>
        ` : ''}

        ${sections.diagnoses && data?.diagnoses?.length ? `
        <div class="section">
          <div class="section-title">Diagnoses</div>
          <table>
            <tr><th>ICD-10</th><th>Description</th><th>Type</th></tr>
            ${data.diagnoses.map(d => `
              <tr>
                <td>${d.code}</td>
                <td>${d.description}</td>
                <td>${d.primary ? 'Primary' : 'Secondary'}</td>
              </tr>
            `).join('')}
          </table>
        </div>
        ` : ''}

        ${sections.medications && data?.medications?.length ? `
        <div class="section">
          <div class="section-title">Current Medications</div>
          <table>
            <tr><th>Medication</th><th>Dose</th><th>Frequency</th><th>Route</th></tr>
            ${data.medications.map(m => `
              <tr>
                <td>${m.name}</td>
                <td>${m.dose}</td>
                <td>${m.frequency}</td>
                <td>${m.route}</td>
              </tr>
            `).join('')}
          </table>
        </div>
        ` : ''}

        ${sections.vitals && data?.vitals ? `
        <div class="section">
          <div class="section-title">Latest Vital Signs</div>
          <div class="info-grid">
            <div class="info-item"><span class="info-label">Blood Pressure:</span><span class="info-value">${data.vitals.bp || 'N/A'}</span></div>
            <div class="info-item"><span class="info-label">Heart Rate:</span><span class="info-value">${data.vitals.hr || 'N/A'} bpm</span></div>
            <div class="info-item"><span class="info-label">Respiratory Rate:</span><span class="info-value">${data.vitals.rr || 'N/A'} /min</span></div>
            <div class="info-item"><span class="info-label">Temperature:</span><span class="info-value">${data.vitals.temp || 'N/A'} °F</span></div>
            <div class="info-item"><span class="info-label">SpO2:</span><span class="info-value">${data.vitals.spo2 || 'N/A'}</span></div>
            <div class="info-item"><span class="info-label">Pain Level:</span><span class="info-value">${data.vitals.pain || 'N/A'}</span></div>
          </div>
        </div>
        ` : ''}

        ${sections.careTeam && data?.care_team?.length ? `
        <div class="section">
          <div class="section-title">Care Team</div>
          <table>
            <tr><th>Role</th><th>Name</th></tr>
            ${data.care_team.map(c => `
              <tr><td>${c.role}</td><td>${c.name}</td></tr>
            `).join('')}
          </table>
        </div>
        ` : ''}

        ${sections.recentVisits && data?.recent_visits?.length ? `
        <div class="section">
          <div class="section-title">Recent Visits</div>
          <table>
            <tr><th>Date</th><th>Type</th><th>Provider</th><th>Notes</th></tr>
            ${data.recent_visits.map(v => `
              <tr>
                <td>${v.date}</td>
                <td>${v.type}</td>
                <td>${v.provider}</td>
                <td>${v.notes}</td>
              </tr>
            `).join('')}
          </table>
        </div>
        ` : ''}

        ${sections.carePlan && data?.care_plan ? `
        <div class="section">
          <div class="section-title">Care Plan Summary</div>
          <p><strong>Goals:</strong> ${data.care_plan.goals?.join(', ') || 'N/A'}</p>
          <p style="margin-top: 8px;"><strong>Interventions:</strong> ${data.care_plan.interventions?.join(', ') || 'N/A'}</p>
        </div>
        ` : ''}

        ${sections.alerts && data?.alerts?.length ? `
        <div class="section">
          <div class="section-title">Active Alerts</div>
          ${data.alerts.map(a => `
            <div class="alert ${a.priority === 'high' ? 'alert-high' : ''}">
              <strong>${a.type}:</strong> ${a.message}
            </div>
          `).join('')}
        </div>
        ` : ''}

        <div class="footer">
          <p>CONFIDENTIAL - This document contains protected health information (PHI)</p>
          <p>Goodwin Cares Hospice • Generated by Hospice Operating System</p>
        </div>
      </body>
      </html>
    `;
  };

  const handleDownloadPDF = () => {
    toast.info("PDF download feature coming soon");
  };

  const handleShareViaEmail = async () => {
    setSharing(true);
    try {
      const data = summaryData;
      const subject = encodeURIComponent(`Patient Summary - ${data?.patient?.name || patientName}`);
      const body = encodeURIComponent(
        `Patient Summary for ${data?.patient?.name || patientName}\n\n` +
        `Generated: ${new Date().toLocaleDateString()}\n\n` +
        `Primary Diagnosis: ${data?.patient?.primary_diagnosis || 'N/A'}\n` +
        `PPS Score: ${data?.patient?.pps_score || 'N/A'}%\n` +
        `Attending Physician: ${data?.patient?.attending_physician || 'N/A'}\n\n` +
        `---\nThis summary was generated by Goodwin Cares Hospice Operating System.\n` +
        `For full patient details, please log in to the portal.`
      );
      
      window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
      toast.success("Email client opened");
    } catch (error) {
      toast.error("Failed to open email client");
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      const shareUrl = `${window.location.origin}/patient-summary/${patientId || 'demo'}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard", {
        description: "Share this link with authorized personnel"
      });
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleShareToReferral = () => {
    const data = summaryData;
    const referralText = 
      `PATIENT REFERRAL SUMMARY\n` +
      `========================\n\n` +
      `Patient: ${data?.patient?.name || patientName}\n` +
      `DOB: ${data?.patient?.dob || 'N/A'}\n` +
      `Primary Dx: ${data?.patient?.primary_diagnosis || 'N/A'}\n` +
      `PPS Score: ${data?.patient?.pps_score || 'N/A'}%\n\n` +
      `Current Medications:\n` +
      (data?.medications?.map(m => `• ${m.name} ${m.dose} ${m.frequency}`).join('\n') || 'N/A') +
      `\n\nLatest Vitals:\n` +
      `• BP: ${data?.vitals?.bp || 'N/A'}\n` +
      `• HR: ${data?.vitals?.hr || 'N/A'} bpm\n` +
      `• SpO2: ${data?.vitals?.spo2 || 'N/A'}\n\n` +
      `---\nGenerated by Goodwin Cares HOS`;
    
    navigator.clipboard.writeText(referralText);
    toast.success("Referral summary copied!", {
      description: "Ready to paste into referral communication"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
        <span className="ml-3 text-slate-400">Loading patient summary...</span>
      </div>
    );
  }

  const sectionOptions = [
    { key: 'demographics', label: 'Demographics', icon: User },
    { key: 'diagnoses', label: 'Diagnoses', icon: ClipboardList },
    { key: 'medications', label: 'Medications', icon: Pill },
    { key: 'vitals', label: 'Vital Signs', icon: Activity },
    { key: 'careTeam', label: 'Care Team', icon: Users },
    { key: 'recentVisits', label: 'Recent Visits', icon: Calendar },
    { key: 'carePlan', label: 'Care Plan', icon: Heart },
    { key: 'alerts', label: 'Alerts', icon: AlertTriangle }
  ];

  return (
    <div className="space-y-6" data-testid="print-patient-summary">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-500/20 rounded-lg">
            <Printer className="w-6 h-6 text-teal-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white">Print Patient Summary</h2>
            <p className="text-sm text-slate-400">
              Select sections to include in the printout
            </p>
          </div>
        </div>
        <Badge className="bg-teal-500/20 text-teal-300 border-0">
          {patientName}
        </Badge>
      </div>

      <Separator className="bg-slate-700" />

      {/* Section Selection */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <FileText className="w-4 h-4 text-teal-400" />
            Select Sections to Print
          </CardTitle>
          <CardDescription className="text-slate-400">
            Choose which information to include in the printed summary
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sectionOptions.map(({ key, label, icon: Icon }) => (
              <div
                key={key}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedSections[key]
                    ? 'bg-teal-500/20 border-teal-500/50'
                    : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                }`}
                onClick={() => toggleSection(key)}
                data-testid={`section-toggle-${key}`}
              >
                <Checkbox
                  checked={selectedSections[key]}
                  onCheckedChange={() => toggleSection(key)}
                  className="border-slate-500"
                />
                <Icon className={`w-4 h-4 ${selectedSections[key] ? 'text-teal-400' : 'text-slate-400'}`} />
                <Label className={`cursor-pointer ${selectedSections[key] ? 'text-white' : 'text-slate-300'}`}>
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-white">Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px] rounded-lg bg-white p-4" ref={printRef}>
            <div className="text-slate-900">
              <div className="border-b-2 border-teal-500 pb-3 mb-4">
                <h1 className="text-lg font-bold text-teal-600">Patient Summary Report</h1>
                <p className="text-xs text-slate-500">
                  Generated: {new Date().toLocaleDateString()} | Hospice Care System
                </p>
              </div>

              {selectedSections.demographics && summaryData?.patient && (
                <div className="mb-4">
                  <h2 className="font-semibold text-teal-600 border-b border-slate-200 pb-1 mb-2">
                    Patient Demographics
                  </h2>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="font-medium">Name:</span> {summaryData.patient.name}</div>
                    <div><span className="font-medium">DOB:</span> {summaryData.patient.dob}</div>
                    <div><span className="font-medium">PPS Score:</span> {summaryData.patient.pps_score}%</div>
                    <div><span className="font-medium">Primary Dx:</span> {summaryData.patient.primary_diagnosis}</div>
                  </div>
                </div>
              )}

              {selectedSections.medications && summaryData?.medications?.length > 0 && (
                <div className="mb-4">
                  <h2 className="font-semibold text-teal-600 border-b border-slate-200 pb-1 mb-2">
                    Current Medications
                  </h2>
                  <ul className="text-sm space-y-1">
                    {summaryData.medications.slice(0, 3).map((med, idx) => (
                      <li key={idx}>• {med.name} {med.dose} {med.frequency}</li>
                    ))}
                    {summaryData.medications.length > 3 && (
                      <li className="text-slate-500">...and {summaryData.medications.length - 3} more</li>
                    )}
                  </ul>
                </div>
              )}

              {selectedSections.vitals && summaryData?.vitals && (
                <div className="mb-4">
                  <h2 className="font-semibold text-teal-600 border-b border-slate-200 pb-1 mb-2">
                    Latest Vitals
                  </h2>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>BP: {summaryData.vitals.bp}</div>
                    <div>HR: {summaryData.vitals.hr} bpm</div>
                    <div>SpO2: {summaryData.vitals.spo2}</div>
                  </div>
                </div>
              )}

              <p className="text-xs text-slate-400 mt-4 pt-2 border-t text-center">
                CONFIDENTIAL - Protected Health Information
              </p>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </Button>
        
        {/* Share Summary Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              disabled={sharing}
              data-testid="share-summary-btn"
            >
              {sharing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Share2 className="w-4 h-4 mr-2" />
              )}
              Share Summary
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-slate-800 border-slate-700">
            <DropdownMenuItem 
              onClick={handleShareViaEmail}
              className="text-slate-200 hover:bg-slate-700 cursor-pointer"
            >
              <Mail className="w-4 h-4 mr-2 text-blue-400" />
              Email Summary
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleCopyLink}
              className="text-slate-200 hover:bg-slate-700 cursor-pointer"
            >
              <Link className="w-4 h-4 mr-2 text-green-400" />
              Copy Share Link
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleShareToReferral}
              className="text-slate-200 hover:bg-slate-700 cursor-pointer"
            >
              <Copy className="w-4 h-4 mr-2 text-amber-400" />
              Copy for Referral
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        
        <Button
          variant="outline"
          onClick={handleDownloadPDF}
          className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        <Button
          onClick={handlePrint}
          disabled={printing || !Object.values(selectedSections).some(v => v)}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="print-btn"
        >
          {printing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Printing...
            </>
          ) : (
            <>
              <Printer className="w-4 h-4 mr-2" />
              Print Summary
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
